import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/client';
import LeftSection from './component/LeftSection';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ScheduleIcon from '@mui/icons-material/Schedule';
import useExpiryWarning from '../../../ExpiryWarning/useExpiryWarning';
// import ExpiryWarningModal from '../../../ExpiryWarning/ExpiryWarningModal';
import ExpirySection from './component/ExpirySection';
import CreditExpiryModal from './component/CreditExpiryModal';
import TotalCreditsInfoModal from './component/TotalCreditsInfoModal';
import CurrentBalanceInfoModal from './component/CurrentBalanceInfoModal';
import InfoIcon from '@mui/icons-material/Info';
import LanguageWorldMap from './component/LanguageWorldMap';
import PieChartIcon from '@mui/icons-material/PieChart';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from 'recharts';

export default function SuperAdminDashboard({ onLogout }) {
  const { t } = useTranslation();
  const [cardData, setCardData] = useState({
    remainingCredit: 0,
    usedCredits: 0,
    totalCredits: 0
  });
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyList, setCompanyList] = useState([]);
  const [interviewInsights, setInterviewInsights] = useState({
    total_scheduled: 0,
    completed: 0,
    pending: 0,
    in_progress: 0,
    expired: 0,
    expired_items: [],
    by_department: []
  });
  const [activeUsers, setActiveUsers] = useState(0);
  const [activeTab, setActiveTab] = useState('chart');
  const [deptTab, setDeptTab] = useState('pie');

  const [expiryData, setExpiryData] = useState({
    latest_expiry_date: null,
    nearest_expiry_date: null,
    nearest_expiry_amount: 0,
    total_active_expiring: 0,
    contributing_buckets: [],
    total_expiring_credits: 0,
    upcoming_expiries: [],
    expired_credits: [],
    current_time: null
  });
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showCreditsInfoModal, setShowCreditsInfoModal] = useState(false);
  const [showBalanceInfoModal, setShowBalanceInfoModal] = useState(false);
  const [numericCardData, setNumericCardData] = useState({
    remainingCredit: 0,
    usedCredits: 0,
    totalCredits: 0
  });
  const [availableBalance, setAvailableBalance] = useState(0);
  const [activeNextExpiry, setActiveNextExpiry] = useState(null);
  const role = localStorage.getItem('role');
  const {
    refetchExpiryInfo
  } = useExpiryWarning();

  const fetchCurrentBalance = useCallback(async () => {
    try {
      const userRole = localStorage.getItem('role');

      if (userRole === 'Candidate') {
        const res = await api.get('observability/user-credits/');
        const data = res.data.results || res.data;
        const balance = data.current_balance ?? 0;

        setAvailableBalance(balance);
        setCardData({
          remainingCredit: `${balance} ${t('dashboard.credits')}`,
          usedCredits: `${data.used_credits || 0} ${t('dashboard.credits')}`,
          totalCredits: `${data.lifetime_credits ?? 0} ${t('dashboard.credits')}`
        });
        setNumericCardData({
          remainingCredit: balance,
          usedCredits: data.used_credits ?? 0,
          totalCredits: data.lifetime_credits ?? 0
        });
        if (data.company_name) {
          setCompanyList(prev => {
            if (prev.length === 1 && prev[0].id === (data.company_id || data.company) && prev[0].name === data.company_name) {
              return prev;
            }
            return [{ id: data.company_id || data.company, name: data.company_name }];
          });
        }
      } else {
        // Fetch observability data first to resolve mappedCompanies
        const obsRes = await api.get('observability/credits/');
        const data = obsRes.data.results || obsRes.data;

        // Map unique companies as objects
        const mappedCompanies = Array.from(
          new Map(
            data
              .filter(d => d.company && d.company_name)
              .map(d => [d.company, { id: d.company, name: d.company_name }])
          ).values()
        );
        setCompanyList(prev => {
          if (prev.length !== mappedCompanies.length) return mappedCompanies;
          const isSame = prev.every((p, i) => p.id === mappedCompanies[i].id && p.name === mappedCompanies[i].name);
          return isSame ? prev : mappedCompanies;
        });

        const selectedCompanyObj = mappedCompanies.find(c => c.name === selectedCompany);
        const params = {};
        if (selectedCompanyObj) {
          params.company_id = selectedCompanyObj.id;
        }

        // Fetch available balance and lifetime totals in parallel
        const [balanceRes, lifetimeRes] = await Promise.all([
          api.get('credits/balance/', { params }),
          userRole === 'Super Admin' ? Promise.resolve(null) : api.get('credits/lifetime/').catch(() => null)
        ]);

        const balanceVal = balanceRes.data?.current_balance ?? 0;
        const nextExpiry = balanceRes.data?.next_expiry ?? null;

        // Use lifetime API for B2B/Candidates; for Super Admin use observability totals
        const lifetimeData = lifetimeRes?.data;
        const lifetimeTotalAdded = lifetimeData?.lifetime_total_added;
        const lifetimeTotalUsed = lifetimeData?.lifetime_total_used;

        const filteredData =
          selectedCompany && selectedCompany !== t('dashboard.allCompanies')
            ? data.filter(d => d.company_name === selectedCompany)
            : data;

        const totals = filteredData.reduce(
          (acc, curr) => {
            acc.total_credits += curr.total_credits;
            acc.used_credits += curr.used_credits;
            acc.remaining_credits += curr.remaining_credits || 0;
            return acc;
          },
          { total_credits: 0, used_credits: 0, remaining_credits: 0 }
        );

        const totalCreditsValue = userRole === 'Super Admin' ? totals.total_credits : (lifetimeTotalAdded ?? totals.total_credits);
        const usedCreditsValue = userRole === 'Super Admin' ? totals.used_credits : (lifetimeTotalUsed ?? totals.used_credits);
        const balance = userRole === 'Super Admin'
          ? (selectedCompany && selectedCompany !== t('dashboard.allCompanies') ? balanceVal : totals.remaining_credits)
          : balanceVal;

        setAvailableBalance(balance);
        if (userRole !== 'Super Admin' || (selectedCompany && selectedCompany !== t('dashboard.allCompanies'))) {
          setActiveNextExpiry(nextExpiry);
        }

        setCardData({
          remainingCredit: `${balance} ${t('dashboard.credits')}`,
          usedCredits: `${usedCreditsValue} ${t('dashboard.credits')}`,
          totalCredits: `${totalCreditsValue} ${t('dashboard.credits')}`
        });
        setNumericCardData({
          remainingCredit: balance,
          usedCredits: usedCreditsValue,
          totalCredits: totalCreditsValue
        });
      }
    } catch {
      setAvailableBalance(0);
      setActiveNextExpiry(null);
      setCardData({
        remainingCredit: `0 ${t('dashboard.credits')}`,
        usedCredits: `0 ${t('dashboard.credits')}`,
        totalCredits: `0 ${t('dashboard.credits')}`
      });
      setNumericCardData({
        remainingCredit: 0,
        usedCredits: 0,
        totalCredits: 0
      });
      setCompanyList([]);
    }
  }, [selectedCompany, t]);

  const normalizeInterviewInsights = useCallback((raw) => {
    const deriveTotalScheduled = (data) =>
      (data.completed || 0) +
      (data.pending || 0) +
      (data.in_progress || 0) +
      (data.expired || 0);

    if (!raw) {
      return {
        total_scheduled: 0,
        completed: 0,
        pending: 0,
        in_progress: 0,
        expired: 0,
        expired_items: [],
        by_department: []
      };
    }
    if (!Array.isArray(raw)) {
      const normalized = {
        completed: raw.completed || 0,
        pending: raw.pending || 0,
        in_progress: raw.in_progress || 0,
        expired: raw.expired || 0,
        expired_items: Array.isArray(raw.expired_items) ? raw.expired_items : [],
        by_department: raw.by_department || []
      };
      return {
        total_scheduled: deriveTotalScheduled(normalized),
        ...normalized
      };
    }
    const merged = raw.reduce(
      (a, c) => ({
        completed: a.completed + (c.completed || 0),
        pending: a.pending + (c.pending || 0),
        in_progress: a.in_progress + (c.in_progress || 0),
        expired: a.expired + (c.expired || 0),
        expired_items: [
          ...a.expired_items,
          ...(Array.isArray(c.expired_items) ? c.expired_items : [])
        ]
      }),
      {
        completed: 0,
        pending: 0,
        in_progress: 0,
        expired: 0,
        expired_items: []
      }
    );
    merged.total_scheduled = deriveTotalScheduled(merged);
    merged.expired_items = merged.expired_items.slice(0, 200);
    merged.by_department =
      raw.find((x) => x.by_department?.length)?.by_department || raw[0]?.by_department || [];
    return merged;
  }, []);

  const fetchInterviewInsights = useCallback(async () => {
    try {
      const selectedCompanyObj = companyList.find(c => c.name === selectedCompany);
      const params = {};
      if (selectedCompanyObj) {
        params.company_id = selectedCompanyObj.id;
      }
      const res = await api.get('observability/interview-insights/', { params });
      const payload = res.data.results || res.data;
      const data = normalizeInterviewInsights(payload);
      setInterviewInsights({
        total_scheduled: data.total_scheduled || 0,
        completed: data.completed || 0,
        pending: data.pending || 0,
        in_progress: data.in_progress || 0,
        expired: data.expired || 0,
        expired_items: data.expired_items || [],
        by_department: data.by_department || []
      });
    } catch { }
  }, [selectedCompany, companyList, normalizeInterviewInsights]);

  const fetchActiveUsers = useCallback(async () => {
    try {
      const selectedCompanyObj = companyList.find(c => c.name === selectedCompany);
      const params = {};
      if (selectedCompanyObj) {
        params.company_id = selectedCompanyObj.id;
      }
      const res = await api.get('admin/candidates/', { params });
      setActiveUsers(res.data?.pagination?.total_count || 0);
    } catch { }
  }, [selectedCompany, companyList]);

  const fetchExpiryData = useCallback(async () => {
    try {
      const role = localStorage.getItem('role');
      const selectedCompanyObj = companyList.find(c => c.name === selectedCompany);
      const params = {};
      if (selectedCompanyObj) {
        params.company_id = selectedCompanyObj.id;
      }
      
      let balancePromise;
      if (role === 'Candidate') {
        balancePromise = api.get('observability/user-credits/');
      } else {
        balancePromise = api.get('credits/balance/', { params });
      }

      const [balanceRes, summaryRes] = await Promise.all([
        balancePromise,
        api.get('observability/expiry-summary/', { params })
      ]);

      const balanceData = balanceRes.data?.results || balanceRes.data || {};
      const summaryData = summaryRes.data || {};

      const isSuperAdminAllCompanies = role === 'Super Admin' && (!selectedCompany || selectedCompany === t('dashboard.allCompanies'));
      
      const nextExpiry = isSuperAdminAllCompanies
        ? (summaryData.upcoming_expiries?.[0]?.expiry_date || null)
        : (balanceData.next_expiry || null);
        
      const nextExpiryAmount = isSuperAdminAllCompanies
        ? (summaryData.upcoming_expiries?.[0]?.amount || 0)
        : (balanceData.next_expiry_amount ?? 0);

      let totalActiveExpiring = 0;
      if (role === 'Candidate') {
        totalActiveExpiring = balanceData.current_balance ?? 0;
      } else if (isSuperAdminAllCompanies) {
        totalActiveExpiring = summaryData.total_expiring_credits ?? 0;
      } else {
        const contributing = balanceData.contributing_buckets || [];
        totalActiveExpiring = contributing.reduce((sum, b) => sum + (b.remaining_balance || 0), 0);
      }

      setExpiryData({
        ...summaryData,
        nearest_expiry_date: nextExpiry,
        nearest_expiry_amount: nextExpiryAmount,
        total_active_expiring: totalActiveExpiring,
        contributing_buckets: balanceData.contributing_buckets || []
      });

      if (isSuperAdminAllCompanies) {
        setActiveNextExpiry(nextExpiry);
      }
    } catch { }
  }, [selectedCompany, companyList, t]);

  useEffect(() => {
    fetchCurrentBalance();
    fetchInterviewInsights();
    fetchExpiryData();
    if (refetchExpiryInfo) refetchExpiryInfo();
    if (role !== "Candidate") fetchActiveUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, role]);

  const handleCompanyChange = (event) => setSelectedCompany(event.target.value);

  const statsCards = [
    {
      label: t('dashboard.totalCredits'),
      value: cardData.totalCredits,
      icon: <PeopleAltIcon fontSize="small" />,
      color: '#8b5cf6'
    },
    {
      label: t('dashboard.currentBalance'),
      value: cardData.remainingCredit,
      icon: <CreditCardIcon fontSize="small" />,
      color: '#3b82f6'
    },
    {
      label: t('dashboard.latestExpiry'),
      value: activeNextExpiry
        ? new Date(activeNextExpiry).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
        : t('dashboard.noExpiry'),
      icon: <ScheduleIcon fontSize="small" />,
      color: '#f59e0b'
    },
    ...(role !== "Candidate" ? [{
      label: role === "Company Admin"
        ? t('dashboard.activeCandidates')
        : t('dashboard.activeUsers'),
      value: activeUsers,
      icon: <PeopleAltIcon fontSize="small" />,
      color: '#10b981'
    }] : [])
  ];

  return (
    <div className="min-h-screen w-full overflow-hidden" style={{
      background: 'linear-gradient(135deg, #f0f9ff 0%, #fafafa 100%)',
      fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    }}>
      {/* <ExpiryWarningModal
        show={showExpiryWarning}
        onClose={dismissWarning}
        expiryData={expiryWarningData}
      /> */}

      <div className="p-4 md:p-6 w-full max-w-full">
        {/* Header */}
        <div className="mb-4">
          <div
            className="w-full px-4 py-3 rounded-xl text-white"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1 min-w-60">
                <h1 className="text-lg font-semibold mb-0.5 text-white">
                  {t('dashboard.overview')}
                </h1>
                <p className="text-xs text-blue-100">
                  {t('dashboard.subtitle')}
                </p>
              </div>

              {role !== "Candidate" && (
                <>
                  {companyList.length === 1 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-blue-100 uppercase tracking-wide">
                        {t('dashboard.company')}:
                      </span>
                      <span className="text-sm font-bold text-white">
                        {companyList[0]?.name}
                      </span>
                    </div>
                  ) : (
                    <div className="min-w-60">
                      <select
                        value={selectedCompany}
                        onChange={handleCompanyChange}
                        className="w-full px-3 py-1.5 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm bg-white text-blue-600 border-0 shadow-sm"
                        style={{
                          fontFamily: "'Poppins', sans-serif"
                        }}
                      >
                        <option value="">{t('dashboard.allCompanies')}</option>
                        {companyList.map((company, index) => (
                          <option key={index} value={company.name}>{company.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Row 1 — Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {statsCards.map((card, idx) => (
            <div
              key={idx}
              className="rounded-xl p-3 transition-all duration-300 cursor-pointer group"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(229, 229, 229, 0.3)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
              }}
              onClick={() => {
                if (card.label === t('dashboard.latestExpiry')) {
                  setActiveTab('expiry');
                } else if (card.label === t('dashboard.totalCredits')) {
                  setShowCreditsInfoModal(true);
                } else if (card.label === t('dashboard.currentBalance')) {
                  setShowBalanceInfoModal(true);
                }
              }}
            >
              <div className="flex justify-between items-center mb-2">
                <span
                  className="font-semibold uppercase tracking-wide text-xs"
                  style={{ color: '#525252' }}
                >
                  {card.label}
                </span>
                <div
                  className="p-1.5 rounded-lg transition-all duration-200"
                  style={{
                    backgroundColor: `${card.color}15`,
                    color: card.color
                  }}
                >
                  {card.icon}
                </div>
              </div>
              <div
                className="text-xl font-bold"
                style={{ color: '#171717' }}
              >
                {card.value}
              </div>
            </div>
          ))}
        </div>

        {/* Row 2 — Quick Insights */}
        <div
          className="rounded-xl p-3 mb-4 transition-all duration-300"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(229, 229, 229, 0.3)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}
        >
          <div className="flex items-center mb-3 gap-2">
            <div
              className="p-1.5 rounded-lg"
              style={{
                background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(212, 70, 239, 0.1) 100%)'
              }}
            >
              <span className="text-base">📊</span>
            </div>
            <h3 className="text-sm font-bold" style={{ color: '#171717' }}>
              {t('dashboard.interviewInsights')}
            </h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {[
              { label: t('dashboard.totalScheduled'), value: interviewInsights.total_scheduled, color: '#0ea5e9', gradient: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)' },
              { label: t('dashboard.completed'), value: interviewInsights.completed, color: '#d946ef', gradient: 'linear-gradient(135deg, rgba(212, 70, 239, 0.1) 0%, rgba(212, 70, 239, 0.05) 100%)' },
              { label: t('dashboard.pending'), value: interviewInsights.pending, color: '#f59e0b', gradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)' },
              { label: t('dashboard.inProgress'), value: interviewInsights.in_progress, color: '#10b981', gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)' },
              { label: t('dashboard.expired'), value: interviewInsights.expired, color: '#ef4444', gradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)' }
            ].map((item, idx) => (
              <div
                key={idx}
                className="text-center p-3 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  background: item.gradient,
                  border: `1px solid ${item.color}30`
                }}
              >
                <div
                  className="text-xl font-bold mb-1"
                  style={{ color: item.color }}
                >
                  {item.value}
                </div>
                <div
                  className="text-xs font-semibold"
                  style={{ color: '#525252' }}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 3 — Three Columns Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {/* Language Coverage Map - Spans 2 cols on tablet, 1 on desktop */}
          <div className="md:col-span-2 lg:col-span-1">
            <LanguageWorldMap />
          </div>

          {/* CREDIT ANALYTICS */}
          <div
            className="rounded-2xl flex flex-col h-[520px] transition-all duration-300"
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(229, 229, 229, 0.3)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Header */}
            <div className="p-5 border-b" style={{ borderColor: 'rgba(229, 229, 229, 0.3)' }}>
              {/* Top Row: Icon + Title */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div
                    className="p-2.5 rounded-xl"
                    style={{
                      background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)'
                    }}
                  >
                    <ShowChartIcon style={{ color: '#0ea5e9' }} />
                  </div>

                  <div>
                    <h3 className="font-bold text-lg leading-tight" style={{ color: '#171717' }}>
                      {t('dashboard.creditAnalytics')}
                    </h3>
                    <p className="text-sm font-medium mt-1" style={{ color: '#737373' }}>
                      {t('dashboard.trackUsageExpiry')}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setShowExpiryModal(true)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 text-[10px] font-bold shadow-sm mr-[-20px]"
                >
                  <InfoIcon style={{ fontSize: '12px' }} />
                  {t('dashboard.expiryDetail')}
                </button>
              </div>

              {/* Bottom Row: Segmented Control */}
              <div
                className="flex rounded-xl p-1 w-fit"
                style={{ background: 'rgba(229, 229, 229, 0.3)' }}
              >
                <button
                  onClick={() => setActiveTab('chart')}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${activeTab === 'chart'
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                  style={{
                    background: activeTab === 'chart' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                    backdropFilter: activeTab === 'chart' ? 'blur(10px)' : 'none',
                    boxShadow: activeTab === 'chart' ? '0 2px 4px rgba(0, 0, 0, 0.05)' : 'none'
                  }}
                >
                  {t('dashboard.usageAnalytics')}
                </button>

                <button
                  onClick={() => setActiveTab('expiry')}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${activeTab === 'expiry'
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                  style={{
                    background: activeTab === 'expiry' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                    backdropFilter: activeTab === 'expiry' ? 'blur(10px)' : 'none',
                    boxShadow: activeTab === 'expiry' ? '0 2px 4px rgba(0, 0, 0, 0.05)' : 'none'
                  }}
                >
                  {t('dashboard.expiringAt')}
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-hidden p-5">
              {activeTab === 'chart' ? <LeftSection selectedCompany={selectedCompany} /> : <ExpirySection expiryData={expiryData} />}
            </div>
          </div>




          {/* RIGHT — BY DEPARTMENT */}
          <div className="flex flex-col">
            {/* BY DEPARTMENT */}
            <div
              className="rounded-2xl p-5 flex flex-col transition-all duration-300 h-[520px]"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(229, 229, 229, 0.3)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}
            >
              {/* Header */}
              <div className="mb-4">
                {/* Title */}
                <span
                  className="block font-bold uppercase tracking-wide text-base mb-3"
                  style={{ color: '#171717' }}
                >
                  {t('dashboard.byDepartment')}
                </span>

                {/* Segmented Tabs */}
                <div
                  className="flex rounded-xl p-1 h-10 w-fit"
                  style={{ background: 'rgba(229, 229, 229, 0.3)' }}
                >
                  <button
                    onClick={() => setDeptTab('bar')}
                    className={`flex items-center justify-center gap-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${deptTab === 'bar'
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    style={{
                      background: deptTab === 'bar' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                      backdropFilter: deptTab === 'bar' ? 'blur(10px)' : 'none',
                      boxShadow: deptTab === 'bar' ? '0 2px 4px rgba(0, 0, 0, 0.05)' : 'none'
                    }}
                  >
                    <ShowChartIcon fontSize="small" />
                    <span className="hidden sm:inline">
                      {t('dashboard.usage')}
                    </span>
                  </button>

                  <button
                    onClick={() => setDeptTab('pie')}
                    className={`flex items-center justify-center gap-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${deptTab === 'pie'
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                    style={{
                      background: deptTab === 'pie' ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
                      backdropFilter: deptTab === 'pie' ? 'blur(10px)' : 'none',
                      boxShadow: deptTab === 'pie' ? '0 2px 4px rgba(0, 0, 0, 0.05)' : 'none'
                    }}
                  >
                    <PieChartIcon fontSize="small" />
                    <span className="hidden sm:inline">
                      {t('dashboard.pie')}
                    </span>
                  </button>
                </div>
              </div>

              {/* Chart Body */}
              <div className="flex-1 min-h-0">
                {deptTab === 'bar'
                  ? <DepartmentBarChart data={interviewInsights.by_department} />
                  : <DepartmentPieChart data={interviewInsights.by_department} />
                }
              </div>
            </div>
          </div>

        </div>

        {/* Modal rendered via Portal */}
        <CreditExpiryModal
          isOpen={showExpiryModal}
          onClose={() => setShowExpiryModal(false)}
          expiryData={expiryData}
        />

        <TotalCreditsInfoModal
          isOpen={showCreditsInfoModal}
          onClose={() => setShowCreditsInfoModal(false)}
          numericData={numericCardData}
        />

        <CurrentBalanceInfoModal
          isOpen={showBalanceInfoModal}
          onClose={() => setShowBalanceInfoModal(false)}
          numericData={numericCardData}
        />
      </div>
    </div>
  );
}

/* BAR CHART */
const DepartmentBarChart = ({ data }) => {
  const COLORS = [
    "#0ea5e9", "#d946ef", "#10b981", "#f59e0b", "#ef4444",
    "#8b5cf6", "#06b6d4", "#f97316", "#ec4899", "#6366f1",
    "#14b8a6", "#a855f7", "#84cc16", "#eab308", "#64748b"
  ];

  const formatted = data.map((d, i) => ({
    name: d.job_description__department__name || `Dept ${i + 1}`,
    value: d.count || 0
  }));

  const getColor = (i) => COLORS[i % COLORS.length];

  // Custom Tooltip to show full department name
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            borderRadius: 12,
            border: '1px solid rgba(229, 229, 229, 0.3)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            padding: '12px 16px',
            fontFamily: "'Poppins', sans-serif"
          }}
        >
          <p style={{ margin: 0, fontWeight: '600', color: '#171717', marginBottom: '4px' }}>
            {data.name}
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: '#525252' }}>
            {data.value} {data.value === 1 ? 'interview' : 'interviews'}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tick component to show full name on hover
  const CustomTick = ({ x, y, payload }) => {
    const name = payload.value || '';
    const truncatedName = name.length > 15 ? `${name.substring(0, 15)}...` : name;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#525252"
          fontSize="11"
          fontFamily="'Poppins', sans-serif"
          transform="rotate(-45)"
        >
          <title>{name}</title>
          {truncatedName}
        </text>
      </g>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={formatted}
        margin={{ top: 10, right: 10, left: 0, bottom: 60 }}
      >
        <CartesianGrid
          stroke="rgba(229, 229, 229, 0.5)"
          strokeDasharray="3 3"
        />

        <XAxis
          dataKey="name"
          tick={<CustomTick />}
          interval={0}
          height={60}
        />

        <YAxis
          tick={{ fontSize: 12, fill: "#525252", fontFamily: "'Poppins', sans-serif" }}
          axisLine={false}
        />

        <Tooltip content={<CustomTooltip />} />

        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
          {formatted.map((_, i) => (
            <Cell key={i} fill={getColor(i)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};


/* PIE CHART */
const DepartmentPieChart = ({ data }) => {
  const { t } = useTranslation();
  const COLORS = [
    "#0ea5e9", "#d946ef", "#10b981", "#f59e0b",
    "#ef4444", "#8b5cf6", "#06b6d4", "#f97316",
    "#ec4899", "#6366f1", "#14b8a6", "#a855f7"
  ];

  const formatted = data.map((d, i) => ({
    name: d.job_description__department__name || `Dept ${i + 1}`,
    value: d.count || 0
  }));

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null; // Hide labels for slices < 5%

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="11"
        fontWeight="600"
        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="relative h-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <defs>
            {COLORS.map((color, idx) => (
              <linearGradient key={idx} id={`gradient${idx}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                <stop offset="100%" stopColor={color} stopOpacity={1} />
              </linearGradient>
            ))}
          </defs>

          <Pie
            data={formatted}
            dataKey="value"
            outerRadius="80%"
            innerRadius="50%"
            paddingAngle={2}
            stroke="#ffffff"
            strokeWidth={2}
            labelLine={false}
            label={CustomLabel}
          >
            {formatted.map((_, idx) => (
              <Cell
                key={idx}
                fill={`url(#gradient${idx % COLORS.length})`}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Pie>

          <Tooltip
            wrapperStyle={{
              borderRadius: 12,
              border: '1px solid rgba(229, 229, 229, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(20px)',
              padding: '12px 16px',
              fontFamily: "'Poppins', sans-serif"
            }}
            contentStyle={{
              border: 'none',
              borderRadius: 12,
              backgroundColor: 'transparent'
            }}
            formatter={(val, name) => [
              <span style={{ fontWeight: '600', color: '#171717', fontFamily: "'Poppins', sans-serif" }}>
                {val} {t('dashboard.interviews')}
              </span>,
              <span style={{ color: '#525252', fontSize: '12px', fontFamily: "'Poppins', sans-serif" }}>
                {name}
              </span>
            ]}
            labelStyle={{ color: '#262626', fontWeight: '600', fontFamily: "'Poppins', sans-serif" }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-wrap justify-center gap-2 px-2 pb-2">
        {formatted.slice(0, 4).map((item, idx) => (
          <div key={idx} className="flex items-center gap-1 text-xs">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
            />
            <span className="text-gray-600 truncate max-w-16" title={item.name}>
              {item.name.length > 8 ? `${item.name.slice(0, 8)}...` : item.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
